// Member Signup Checkbox Web Component
var MemberSignupCheckbox = class extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="checkbox"].checkbox');

    if (this.input) {
      this.input.addEventListener('change', this._onCheckboxChange.bind(this));
    }

    // Prevent accordion toggle when clicking on checkbox area
    this.addEventListener('click', this._preventAccordionToggle.bind(this));
  }

  _preventAccordionToggle(event) {
    // Stop event from bubbling up to the summary element
    event.stopPropagation();
  }

  async _onCheckboxChange(event) {
    const isChecked = event.target.checked;
    const attributeValue = isChecked ? 'true' : '';

    try {
      // Update cart attributes via Shopify Cart API
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            new_member: attributeValue
          }
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const cartContent = await response.json();

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
      }));

      // For main cart page, also reload the page to show updated member pricing
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
      }

    } catch (error) {
      console.error('Error updating member signup:', error);
      // Revert checkbox state on error
      event.target.checked = !isChecked;
    }
  }
};

// Tax Toggle Web Component
var TaxToggle = class extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('input[type="checkbox"]');

    if (this.toggle) {
      this.toggle.addEventListener('change', this._onToggleChange.bind(this));
    }
  }

  async _onToggleChange(event) {
    const isChecked = event.target.checked;
    const attributeValue = isChecked ? 'true' : '';

    try {
      // Update cart attributes via Shopify Cart API
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            taxes_excluded: attributeValue
          }
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      // Reload page to reflect tax changes in Liquid rendering
      window.location.reload();

    } catch (error) {
      console.error('Error updating tax toggle:', error);
      // Revert toggle state on error
      event.target.checked = !isChecked;
    }
  }
};

// Customer Type Selector Web Component
class CustomerTypeSelector extends HTMLElement {
  constructor() {
    super();
    this.context = this.getAttribute('data-context') || 'cart';
    this.selectedType = null;
    this.modal = null;
  }

  connectedCallback() {
    // DOM is ready, now we can initialize
    this.init();
  }

  init() {
    this.radioButtons = this.querySelectorAll('input[type="radio"]');
    this.checkoutButtons = this.getCheckoutButtons();

    // If no radio buttons exist (customer is logged in or B2B signup completed), enable checkout immediately
    if (this.radioButtons.length === 0) {
      this.enableCheckout();
      return;
    }

    // Check if B2B signup was completed (from sessionStorage)
    if (this.hasB2BSignupCompleted()) {
      // Pre-select B2B option
      const b2bRadio = this.querySelector('input[value="b2b"]');
      if (b2bRadio) {
        b2bRadio.checked = true;
        this.selectedType = 'b2b';
      }
      // Enable checkout immediately (don't open modal)
      this.enableCheckout();
      
      // Still add event listeners so user can change their mind
      this.radioButtons.forEach(radio => {
        radio.addEventListener('change', this.handleSelectionChange.bind(this));
      });
      
      // Intercept checkout button clicks
      this.interceptCheckoutButtons();
      return;
    }

    // Disable checkout buttons initially
    this.disableCheckout();

    // Add event listeners
    this.radioButtons.forEach(radio => {
      radio.addEventListener('change', this.handleSelectionChange.bind(this));
    });

    // Intercept checkout button clicks
    this.interceptCheckoutButtons();
  }

  hasB2BSignupCompleted() {
    try {
      const data = sessionStorage.getItem('b2b_signup_data');
      if (!data) return false;
      
      const signupData = JSON.parse(data);
      return signupData.completed === true;
    } catch (error) {
      return false;
    }
  }

  getCheckoutButtons() {
    if (this.context === 'drawer') {
      return document.querySelectorAll('#cart-drawer button[name="checkout"], #cart-drawer a[href*="checkout"]');
    } else {
      return document.querySelectorAll('button[name="checkout"], .cart-form button[type="submit"]');
    }
  }

  disableCheckout() {
    this.checkoutButtons.forEach(button => {
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.cursor = 'not-allowed';
    });
  }

  enableCheckout() {
    this.checkoutButtons.forEach(button => {
      button.disabled = false;
      button.style.opacity = '';
      button.style.cursor = '';
    });
  }

  handleSelectionChange(event) {
    this.selectedType = event.target.value;
    this.clearError();

    if (this.selectedType === 'private') {
      // Clear any B2B data when switching to private
      this.clearB2BData();
      this.enableCheckout();
    } else if (this.selectedType === 'b2b') {
      // Check if B2B signup already completed (sessionStorage exists)
      if (this.hasB2BSignupCompleted()) {
        // Don't open modal, just enable checkout
        this.enableCheckout();
      } else {
        // Not completed yet, open modal for signup
        this.openB2BModal();
      }
    }
  }

  clearB2BData() {
    // Clear sessionStorage
    try {
      sessionStorage.removeItem('b2b_signup_data');
    } catch (error) {
      console.error('Error clearing B2B sessionStorage:', error);
    }

    // Clear VAT ID from cart attributes
    this.clearVatIdFromCart();
  }

  async clearVatIdFromCart() {
    try {
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            vat_id: ''
          }
        })
      });

      // Don't trigger cart:refresh event here to avoid re-rendering the customer type selector
      // The VAT ID is not visible in the UI, so no refresh needed
    } catch (error) {
      console.error('Error clearing VAT ID from cart:', error);
    }
  }

  openB2BModal() {
    // Disable checkout until B2B form is completed
    this.disableCheckout();
    // Create or show B2B modal
    if (!this.modal) {
      this.createB2BModal();
    }
    this.modal.show();
  }

  createB2BModal() {
    // Find pre-rendered B2B modal in the DOM (from overlay-group)
    this.modal = document.querySelector('#b2b-modal');

    if (this.modal) {

      // Listen for modal completion
      this.modal.addEventListener('b2b-completed', () => {
        this.enableCheckout();
      });

      // Listen for modal cancellation
      this.modal.addEventListener('b2b-cancelled', () => {
        this.clearB2BData();
        this.resetSelection();
      });
    } else {
      console.error('B2B modal not found. Please add b2b-signup-modal section to overlay-group.');
    }
  }

  resetSelection() {
    this.radioButtons.forEach(radio => radio.checked = false);
    this.selectedType = null;
    this.disableCheckout();
  }

  interceptCheckoutButtons() {
    this.checkoutButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        if (!this.selectedType) {
          event.preventDefault();
          this.showError();
        }
      });
    });
  }

  showError() {
    this.classList.add('customer-type-selector--error');
  }

  clearError() {
    this.classList.remove('customer-type-selector--error');
  }
}


// B2B Modal Web Component - Extends theme's Drawer class
class B2BModal extends window.Drawer {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize success detection and VAT validation when modal is shown
    this.addEventListener('dialog:after-show', () => {
      // Reset flag when modal opens (for new form submission)
      this.formCompleted = false;
      this.initializeSuccessDetection();
      this.initializeVATValidation();
      this.repopulateFormData();
    });

    // Setup VAT error UI button handlers
    this.setupVATErrorHandlers();

    // Check for URL parameter to open modal programmatically
    this.checkURLParameter();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  get openFrom() {
    return 'right'; // Use same as cart-drawer to avoid animation issues
  }

  async hide() {
    // Prevent multiple hide() calls during close animation
    if (this.isHiding) return;
    this.isHiding = true;
    
    // Dispatch event based on whether form was completed
    const eventType = this.formCompleted ? 'b2b-completed' : 'b2b-cancelled';
    this.dispatchEvent(new CustomEvent(eventType, { bubbles: true }));
    
    // Reset flag for next open/close cycle
    this.formCompleted = false;
    
    // Call parent hide() to actually close the modal
    const result = await super.hide();
    
    // Reset guard flag after modal is fully hidden
    this.isHiding = false;
    
    return result;
  }

  initializeSuccessDetection() {
    // Disconnect any existing observers
    if (this.appEmbedObserver) {
      this.appEmbedObserver.disconnect();
    }
    if (this.successObserver) {
      this.successObserver.disconnect();
    }

    // Observer to wait for app embed to be added to DOM
    this.appEmbedObserver = new MutationObserver(() => {
      const appEmbed = this.querySelector('[id*="app-embed"], [class*="app-embed"], form-embed');
      if (appEmbed && appEmbed.shadowRoot) {
        this.setupSuccessDetection(appEmbed);
        this.appEmbedObserver.disconnect();
      }
    });

    // Check if app embed already exists
    const existingAppEmbed = this.querySelector('[id*="app-embed"], [class*="app-embed"], form-embed');
    if (existingAppEmbed && existingAppEmbed.shadowRoot) {
      this.setupSuccessDetection(existingAppEmbed);
    } else {
      this.appEmbedObserver.observe(this, { childList: true, subtree: true });
    }
  }

  setupSuccessDetection(appEmbed) {
    const shadowRoot = appEmbed.shadowRoot;
    if (!shadowRoot) {
      return;
    }

    // The data-current-step attribute is on the first child element in shadow root
    const formRootElement = shadowRoot.querySelector('[data-form-root="true"]');
    
    if (!formRootElement) {
      return;
    }

    // Watch for success state by observing the data-current-step attribute
    this.successObserver = new MutationObserver(async (mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-current-step') {
          const currentStep = formRootElement.getAttribute('data-current-step');
          
          if (currentStep === 'success') {
            // Form submitted successfully!
            await this.handleSuccessfulSubmission();
            this.successObserver.disconnect();
            break;
          }
        }
      }
    });

    // Start observing the form root element for attribute changes
    this.successObserver.observe(formRootElement, { 
      attributes: true,
      attributeFilter: ['data-current-step']
    });
  }

  async handleSuccessfulSubmission() {
    // Mark as completed immediately so manual close doesn't reset selection
    this.formCompleted = true;
    
    // Mark B2B signup as completed in sessionStorage
    this.setB2BSignupCompleted();

    // Extract VAT ID and save to cart attributes
    await this.saveVatIdToCart();

    // Wait 1 second before auto-closing modal
    setTimeout(() => {
      this.hide();
    }, 1000);
  }

  async saveVatIdToCart() {
    // Use the VAT ID that was stored during form submission
    if (!this.submittedVatId) return;

    try {
      // Update cart attributes via Shopify Cart API
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            vat_id: this.submittedVatId
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart with VAT ID');
      }

      // Trigger cart refresh event
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
      }));
    } catch (error) {
      console.error('Error saving VAT ID to cart:', error);
    }
  }

  initializeVATValidation() {
    // Prevent double initialization
    if (this.vatValidationInitialized) {
      return;
    }
    this.vatValidationInitialized = true;

    const observer = new MutationObserver(() => {
      const appEmbed = this.querySelector('[id*="app-embed"], [class*="app-embed"], form-embed');
      if (appEmbed && appEmbed.shadowRoot) {
        this.setupVATFormValidation(appEmbed);
        observer.disconnect();
      }
    });

    // Check if app embed already exists
    const existingAppEmbed = this.querySelector('[id*="app-embed"], [class*="app-embed"], form-embed');
    if (existingAppEmbed && existingAppEmbed.shadowRoot) {
      this.setupVATFormValidation(existingAppEmbed);
    } else {
      observer.observe(this, { childList: true, subtree: true });
    }
  }

  setupVATFormValidation(appEmbed) {
    const shadowRoot = appEmbed.shadowRoot;
    if (!shadowRoot) return;

    const form = shadowRoot.querySelector('form[data-testid="form"]');
    const vatField = shadowRoot.querySelector('#company_tax_id') || shadowRoot.querySelector('#custom\\#cvr_number');

    if (!form || !vatField || form.dataset.vatHandlerAdded) return;

    form.dataset.vatHandlerAdded = 'true';

    let isValidating = false;
    let vatValidationPassed = false;
    let bypassVATValidation = false;

    // Intercept form submission
    form.addEventListener('submit', async (e) => {
      const vatValue = vatField.value.trim();

      // Store VAT value on component for later use in cart attribute update
      this.submittedVatId = vatValue;

      // Capture form data before any validation (for repopulation if needed)
      const formData = this.captureFormData(shadowRoot);
      this.saveFormData(formData);

      // Allow if VAT validation bypassed (user clicked "Continue anyway")
      if (bypassVATValidation) {
        return;
      }

      // Allow if no VAT entered (let Shopify Forms handle required field validation)
      if (!vatValue) {
        return;
      }

      // Allow if VAT validation already passed for this value
      if (vatValidationPassed && vatField.dataset.lastValidatedValue === vatValue) {
        return;
      }

      // Block this submission attempt to validate VAT first
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (isValidating) return;

      isValidating = true;
      vatValidationPassed = false;

      const submitBtn = shadowRoot.querySelector('button[type="submit"]');
      const originalText = submitBtn?.textContent;

      // Validate country code before API call
      const countryCodeError = this.validateVATCountryCode(vatValue);
      if (countryCodeError) {
        this.showVATCountryError(vatField, countryCodeError);
        isValidating = false;
        return;
      }

      try {
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = window.themeVariables?.strings?.vatValidating || 'Validating VAT...';
        }

        const isValid = await validateVAT(vatValue);

        if (isValid) {
          // VAT is valid - allow form submission
          vatValidationPassed = true;
          vatField.dataset.lastValidatedValue = vatValue;
          isValidating = false;
          
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }

          // Re-trigger form submission
          form.requestSubmit();
        } else {
          // VAT is invalid - show error UI
          this.showVATError();
          isValidating = false;
          
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }
        }
      } catch (error) {
        // API error - show error UI (do not allow bypass on API errors)
        this.showVATError();
        isValidating = false;
        
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      }
    }, true); // Use capture phase

    // Clear validation state when VAT field changes
    vatField.addEventListener('input', () => {
      vatValidationPassed = false;
      bypassVATValidation = false;
      delete vatField.dataset.lastValidatedValue;
      // Clear country code error when user types
      this.clearVATCountryError(vatField);
    });

    // Store reference to form and fields for "Continue anyway" button
    this.vatForm = form;
    this.vatField = vatField;
    this.setBypassVATValidation = () => {
      bypassVATValidation = true;
    };
  }

  setupVATErrorHandlers() {
    const tryAgainBtn = this.querySelector('.b2b-modal__try-again');
    const continueAnywayBtn = this.querySelector('.b2b-modal__continue-anyway');

    if (tryAgainBtn) {
      tryAgainBtn.addEventListener('click', () => {
        this.hideVATError();
      });
    }

    if (continueAnywayBtn) {
      continueAnywayBtn.addEventListener('click', () => {
        // Set bypass flag and submit form
        if (this.setBypassVATValidation) {
          this.setBypassVATValidation();
        }
        if (this.vatForm) {
          this.hideVATError();
          this.vatForm.requestSubmit();
        }
      });
    }
  }

  showVATError() {
    const formContainer = this.querySelector('.b2b-modal__form-container');
    const errorContainer = this.querySelector('.b2b-modal__vat-error');

    if (formContainer) formContainer.style.display = 'none';
    if (errorContainer) errorContainer.style.display = 'block';
  }

  hideVATError() {
    const formContainer = this.querySelector('.b2b-modal__form-container');
    const errorContainer = this.querySelector('.b2b-modal__vat-error');

    if (formContainer) formContainer.style.display = '';
    if (errorContainer) errorContainer.style.display = 'none';
  }

  checkURLParameter() {
    // Check if URL contains the parameter to open B2B modal
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('b2b_signup')) {
      // Open modal programmatically
      setTimeout(() => {
        this.show();
      }, 500); // Small delay to ensure DOM is ready
      
      // Remove parameter from URL without reloading page
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('b2b_signup');
      window.history.replaceState({}, '', newUrl);
    }
  }

  getB2BSignupData() {
    try {
      const data = sessionStorage.getItem('b2b_signup_data');
      if (!data) return { completed: false, formData: null };
      
      return JSON.parse(data);
    } catch (error) {
      console.error('Error parsing B2B signup data from sessionStorage:', error);
      return { completed: false, formData: null };
    }
  }

  saveB2BSignupData(updates) {
    try {
      const currentData = this.getB2BSignupData();
      const newData = { ...currentData, ...updates };
      sessionStorage.setItem('b2b_signup_data', JSON.stringify(newData));
    } catch (error) {
      console.error('Error saving B2B signup data to sessionStorage:', error);
    }
  }

  saveFormData(formData) {
    // Store form data in sessionStorage (clears when browser tab/window closes)
    this.saveB2BSignupData({ formData });
  }

  getFormData() {
    const signupData = this.getB2BSignupData();
    return signupData.formData;
  }

  setB2BSignupCompleted() {
    // Mark B2B signup as completed in sessionStorage
    this.saveB2BSignupData({ completed: true });
  }

  repopulateFormData() {
    // Get stored form data from sessionStorage
    const formData = this.getFormData();
    if (!formData) return;

    // Wait for app embed to be in DOM
    const observer = new MutationObserver(() => {
      const appEmbed = this.querySelector('[id*="app-embed"], [class*="app-embed"], form-embed');
      if (appEmbed && appEmbed.shadowRoot) {
        this.fillFormFields(appEmbed.shadowRoot, formData);
        observer.disconnect();
      }
    });

    // Check if app embed already exists
    const existingAppEmbed = this.querySelector('[id*="app-embed"], [class*="app-embed"], form-embed');
    if (existingAppEmbed && existingAppEmbed.shadowRoot) {
      this.fillFormFields(existingAppEmbed.shadowRoot, formData);
    } else {
      observer.observe(this, { childList: true, subtree: true });
    }
  }

  fillFormFields(shadowRoot, formData) {
    if (!shadowRoot || !formData) return;

    // Iterate through saved form data and populate fields
    Object.keys(formData).forEach(fieldId => {
      // Escape special characters in ID for querySelector
      const escapedId = fieldId.replace(/#/g, '\\#');
      const field = shadowRoot.querySelector(`#${escapedId}`);
      
      if (field) {
        field.value = formData[fieldId];
        // Trigger input/change events so the form recognizes the value
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  captureFormData(shadowRoot) {
    if (!shadowRoot) return {};

    const form = shadowRoot.querySelector('form[data-testid="form"]');
    if (!form) return {};

    const formData = {};
    const inputs = form.querySelectorAll('input, textarea, select');

    inputs.forEach(input => {
      if (input.id && input.value) {
        formData[input.id] = input.value;
      }
    });

    return formData;
  }

  validateVATCountryCode(vatNumber) {
    // Get enabled shipping countries from data attribute
    const shippingCountries = this.getAttribute('data-shipping-countries');
    if (!shippingCountries) return null; // No validation if countries not available

    const enabledCountries = shippingCountries.split(',').map(c => c.trim().toUpperCase());
    
    // Extract country code from VAT (first 2 letters)
    const cleanVAT = vatNumber.replace(/\s/g, '').toUpperCase();
    
    // Require at least 2 characters for country code
    if (cleanVAT.length < 2) {
      return window.themeVariables?.strings?.vatCountryMinLength || 'VAT number must be at least 2 characters';
    }
    
    const vatCountryCode = cleanVAT.substring(0, 2);
    
    // Check if VAT country code matches any enabled shipping country
    if (!enabledCountries.includes(vatCountryCode)) {
      if (enabledCountries.length === 1) {
        const message = window.themeVariables?.strings?.vatCountryInvalidSingle || 'VAT number must start with {{country}}';
        return message.replace('{{country}}', enabledCountries[0]);
      } else {
        const message = window.themeVariables?.strings?.vatCountryInvalidMultiple || 'VAT number must start with one of: {{countries}}';
        return message.replace('{{countries}}', enabledCountries.join(', '));
      }
    }
    
    return null; // Valid
  }

  showVATCountryError(vatField, errorMessage) {
    let errorElement = vatField.parentElement.querySelector('.vat-country-error');
    
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'vat-country-error';
      errorElement.style.cssText = 'color: #d72c0d; font-size: 0.875rem; margin-top: 0.5rem;';
      vatField.parentElement.appendChild(errorElement);
    }
    
    errorElement.textContent = errorMessage;
    vatField.setAttribute('aria-invalid', 'true');
    vatField.setAttribute('aria-describedby', 'vat-country-error');
  }

  clearVATCountryError(vatField) {
    const errorElement = vatField.parentElement.querySelector('.vat-country-error');
    if (errorElement) {
      errorElement.remove();
    }
    vatField.removeAttribute('aria-invalid');
    vatField.removeAttribute('aria-describedby');
  }

}

// Register web components
if (!window.customElements.get("member-signup-checkbox")) {
  window.customElements.define("member-signup-checkbox", MemberSignupCheckbox);
}

if (!window.customElements.get("customer-type-selector")) {
  window.customElements.define("customer-type-selector", CustomerTypeSelector);
}

if (!window.customElements.get("b2b-modal")) {
  window.customElements.define("b2b-modal", B2BModal);
}

if (!window.customElements.get("tax-toggle")) {
  window.customElements.define("tax-toggle", TaxToggle);
}

// VAT Validation Helper Functions
async function validateVAT(vatNumber) {
  // Remove spaces and convert to uppercase
  const cleanVAT = vatNumber.replace(/\s/g, '').toUpperCase();

  if (cleanVAT.length < 4) return false;

  try {
    const response = await fetch(`https://api.vatcomply.com/vat?vat_number=${encodeURIComponent(cleanVAT)}`);

    if (response.status === 422) {
      // 422 means invalid VAT number format
      return false;
    }

    if (!response.ok) {
      throw new Error(`VAT API request failed with status ${response.status}`);
    }

    const result = await response.json();
    return result.valid === true;
  } catch (error) {
    // Only allow submission on network errors, not validation errors
    if (error.message.includes('failed with status')) {
      throw error; // Re-throw API errors to be handled by caller
    }
    return true; // Allow submission only on network/fetch errors
  }
}

// Product Image Galleries Component
import { PhotoSwipeLightbox } from "vendor";

class ProductImageGalleries extends HTMLElement {
  constructor() {
    super();
    this.galleries = [];
    this._photoswipe = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Initialize each gallery
    this.querySelectorAll('.product-image-gallery').forEach((gallery, index) => {
      this.initializeGallery(gallery, index);
    });
  }

  initializeGallery(galleryElement, index) {
    const galleryType = galleryElement.getAttribute('data-gallery-type');
    const previewItems = galleryElement.querySelectorAll('.product-image-gallery__preview-item');
    const dataItems = galleryElement.querySelectorAll('.product-image-gallery__data-item');

    // Create data source for PhotoSwipe from the server-rendered data
    const dataSource = Array.from(dataItems).map((item, index) => {
      // Find the corresponding thumbnail image for msrc and dimensions
      const thumbnailImg = previewItems[index]?.querySelector('.product-image-gallery__preview-image');

      return {
        src: item.getAttribute('data-src'),
        msrc: thumbnailImg ? (thumbnailImg.currentSrc || thumbnailImg.src) : item.getAttribute('data-src'),
        alt: item.getAttribute('data-alt') || '',
        width: 0, // Will be set after thumbnails load
        height: 0, // Will be set after thumbnails load
        thumbnailElement: thumbnailImg || null
      };
    });

    // Wait for thumbnail images to load, then update dimensions
    this.waitForThumbnailsAndUpdateDimensions(dataSource, previewItems);

    // Add click handlers to preview items
    previewItems.forEach((item, itemIndex) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.openGallery(dataSource, itemIndex, galleryType);
      });
    });

    // Store gallery data for potential future use
    this.galleries[index] = {
      element: galleryElement,
      type: galleryType,
      dataSource: dataSource
    };
  }

  waitForThumbnailsAndUpdateDimensions(dataSource, previewItems) {
    // Get first 3 thumbnail images
    const thumbnailImages = Array.from(previewItems)
      .slice(0, 3)
      .map(item => item.querySelector('.product-image-gallery__preview-image'))
      .filter(img => img);

    if (thumbnailImages.length === 0) return;

    // Wait for each thumbnail to load
    const loadPromises = thumbnailImages.map((img, index) => {
      return new Promise((resolve) => {
        if (img.complete && img.naturalWidth > 0) {
          // Image already loaded
          resolve({ index, width: img.naturalWidth, height: img.naturalHeight });
        } else {
          // Wait for image to load
          img.onload = () => {
            resolve({ index, width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = () => {
            resolve({ index, width: 0, height: 0 }); // Fallback
          };
        }
      });
    });

    // Update dataSource when thumbnails are loaded
    Promise.all(loadPromises).then((results) => {
      results.forEach(({ index, width, height }) => {
        if (dataSource[index] && width > 0 && height > 0) {
          dataSource[index].width = width;
          dataSource[index].height = height;
        }
      });
    });
  }

  // Create PhotoSwipe instance once (like the theme does)
  get photoswipe() {
    if (this._photoswipe) {
      return this._photoswipe;
    }

    const photoswipe = new PhotoSwipeLightbox({
      pswpModule: () => import('photoswipe'),
      bgOpacity: 1,
      maxZoomLevel: 3,
      closeTitle: window.themeVariables?.strings?.closeGallery || 'Close gallery',
      zoomTitle: window.themeVariables?.strings?.zoomGallery || 'Zoom',
      errorMsg: window.themeVariables?.strings?.errorGallery || 'Image cannot be loaded',
      // UX settings to match theme
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });

    // Handle unknown image sizes for images beyond the first 3 (fallback only)
    photoswipe.on('gettingData', (item) => {
      // The slide data is directly in item.data
      const slideData = item.data;

      // Skip if image already has dimensions (from thumbnails or background loading)
      if (slideData.width > 0 && slideData.height > 0) {
        return;
      }

      if (slideData.width < 1 || slideData.height < 1) { // unknown size
        const img = new Image();

        img.onload = function() {
          slideData.width = this.width; // set image width
          slideData.height = this.height; // set image height

          // Update PhotoSwipe with new dimensions (PhotoSwipe v5 API)
          if (photoswipe.pswp) {
            // Force PhotoSwipe to recalculate dimensions
            photoswipe.pswp.refreshSlideContent(photoswipe.pswp.currIndex);
            photoswipe.pswp.updateSize();
          }
        };

        img.onerror = function() {
          // Set fallback dimensions
          slideData.width = 800;
          slideData.height = 600;
        };

        img.src = slideData.src; // let's download image
      }
    });

    // Add bottom navigation bar (pagination) - same as theme
    photoswipe.on('uiRegister', () => {
      photoswipe.pswp.ui.registerElement({
        name: 'bottom-bar',
        order: 5,
        appendTo: 'wrapper',
        html: `
          <div class="pagination">
            <button class="pagination__item group" rel="prev">
              <span class="animated-arrow animated-arrow--reverse"></span>
            </button>
            <span class="pagination__current text-sm">
              <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
            </span>
            <button class="pagination__item group" rel="next">
              <span class="animated-arrow"></span>
            </button>
          </div>
        `,
        onInit: (el, pswp) => {
          el.querySelector('.pagination__page-count').innerText = pswp.getNumItems();
          el.querySelector('[rel="prev"]')?.addEventListener('click', () => pswp.prev());
          el.querySelector('[rel="next"]')?.addEventListener('click', () => pswp.next());
          pswp.on('change', () => {
            el.querySelector('.pagination__current-page').innerText = pswp.currIndex + 1;
          });
        }
      });
    });

    photoswipe.init();
    return this._photoswipe = photoswipe;
  }

  openGallery(dataSource, startIndex = 0, galleryType = '') {
    // Use the dataSource as-is (dimensions already set in initializeGallery)
    // First 3 images have thumbnail dimensions, rest have 0,0 for background loading
    this.photoswipe.loadAndOpen(startIndex, dataSource);

    // Background load dimensions for remaining images (4+)
    this.loadRemainingDimensions(dataSource);
  }

  loadRemainingDimensions(dataSource) {
    // Find images without dimensions (images 4+ that don't have thumbnail data)
    const unknownItems = dataSource.filter(item => item.width < 1 || item.height < 1);

    if (unknownItems.length === 0) return;

    // Load all remaining images in parallel
    unknownItems.forEach((item) => {
      const img = new Image();

      img.onload = () => {
        // Update the item with real dimensions
        item.width = img.naturalWidth;
        item.height = img.naturalHeight;

        // Update PhotoSwipe if this image is currently visible
        if (this.photoswipe.pswp && this.photoswipe.pswp.currIndex !== undefined) {
          const currentSlide = this.photoswipe.pswp.currSlide;
          // Check if this is the currently visible slide
          if (currentSlide && currentSlide.data.src === item.src) {
            this.photoswipe.pswp.refreshSlideContent(this.photoswipe.pswp.currIndex);
            this.photoswipe.pswp.updateSize();
          }
        }
      };

      img.onerror = () => {
        // Set fallback dimensions
        item.width = 800;
        item.height = 600;
      };

      img.src = item.src;
    });
  }
}

// Register the custom element
if (!window.customElements.get('product-image-galleries')) {
  window.customElements.define('product-image-galleries', ProductImageGalleries);
}

// Custom Shipping Calculator Web Component - adapted from theme's ShippingEstimator
class CustomShippingCalculator extends HTMLElement {
  // API endpoint for shipping rate calculations
  static SHIPPING_RATES_ENDPOINT = '/apps/integration/shipping-rates';

  constructor() {
    super();
    this._estimateShippingListener = this._estimateShipping.bind(this);
  }

  connectedCallback() {
    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.querySelector('.shipping-calculator__results');

    if (this.submitButton) {
      this.submitButton.addEventListener('click', this._estimateShippingListener);
    }
  }

  disconnectedCallback() {
    if (this.submitButton) {
      this.submitButton.removeEventListener('click', this._estimateShippingListener);
    }
  }

  async _estimateShipping(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]')?.value;
    const country = this.querySelector('[name="address[country]"]')?.value;
    const province = this.querySelector('[name="address[province]"]')?.value;

    if (!zip || !country) {
      this._formatError({ general: 'Please fill in all required fields' });
      this.resultsElement.hidden = false;
      return;
    }

    // Get product and variant data
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;

    if (!productId || !variantId) {
      this._formatError({ general: 'Product information not found' });
      this.resultsElement.hidden = false;
      return;
    }

    // Get quantity from product form's quantity selector
    let quantity = 1; // Default fallback
    const quantitySelector = document.querySelector('quantity-selector input[name="quantity"]');
    if (quantitySelector && quantitySelector.value) {
      quantity = parseInt(quantitySelector.value) || 1;
    }

    this.submitButton.setAttribute('aria-busy', 'true');

    try {
      const body = JSON.stringify({
        variantId: variantId,
        quantity: quantity,
        currency: window.Shopify.currency.active,
        locale: window.Shopify.locale,
        shipping_address: { zip, country, province }
      });

      // Use the same custom backend as omhucph theme
      const response = await fetch(CustomShippingCalculator.SHIPPING_RATES_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.shipping_rates) {
        this._formatShippingRates(data.shipping_rates);
      } else {
        this._formatError(data.error || { general: 'Unable to calculate shipping rates' });
      }
    } catch (error) {
      console.error('Shipping calculation error:', error);
      this._formatError({ general: 'Unable to calculate shipping rates. Please try again.' });
    } finally {
      this.resultsElement.hidden = false;
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  _formatShippingRates(shippingRates) {
    let formattedShippingRates = shippingRates.map((shippingRate) => {
      return `<li>${shippingRate["presentment_name"] || shippingRate["name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
    });

    // Use the same strings as the theme's ShippingEstimator
    let message = '';
    if (shippingRates.length === 0) {
      message = window.themeVariables?.strings?.shippingEstimatorNoResults || 'Sorry, we do not ship to your address.';
    } else if (shippingRates.length === 1) {
      message = window.themeVariables?.strings?.shippingEstimatorOneResult || 'There is one shipping rate for your address:';
    } else {
      message = window.themeVariables?.strings?.shippingEstimatorMultipleResults || 'There are several shipping rates for your address:';
    }

    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${message}</p>
        ${formattedShippingRates.length > 0 ? `<ul class="list-disc" role="list">${formattedShippingRates.join("")}</ul>` : ''}
      </div>
    `;
  }

  _formatError(errors) {
    let formattedErrors = [];

    if (typeof errors === 'string') {
      formattedErrors = [`<li>${errors}</li>`];
    } else if (typeof errors === 'object') {
      formattedErrors = Object.keys(errors).map((errorKey) => {
        return `<li>${errors[errorKey]}</li>`;
      });
    }

    const errorMessage = window.themeVariables?.strings?.shippingEstimatorError || 'One or more error occurred while retrieving shipping rates:';

    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${errorMessage}</p>
        <ul class="list-disc" role="list">${formattedErrors.join('')}</ul>
      </div>
    `;
  }
}

if (!window.customElements.get("custom-shipping-calculator")) {
  window.customElements.define("custom-shipping-calculator", CustomShippingCalculator);
}

/* Draft order */
function raqCustomizeFormEmbed(hostElement) {
  const shadowRoot = hostElement.shadowRoot;
  if (!shadowRoot) {
    return;
  }

  /*
  const internalStyle = document.createElement('style');
  internalStyle.textContent = `
    #custom\\#product,
    #label-custom\\#product {
      display: none;
    }
  `;
  shadowRoot.appendChild(internalStyle);
  */

  // set accordion layout
  const appBlockHolder = hostElement.closest('.app-block-holder');
  if (appBlockHolder) {
    const appAccordionHolder = appBlockHolder.querySelector('.app-accordion-holder');
    if (appAccordionHolder) {
      var accordionTitle = '';

      const accordionTitleElement = appAccordionHolder.querySelector('.accordion__toggle span');
      if (accordionTitleElement) {
        var textHeadingElement = shadowRoot.querySelector('h2[class^="_textHeading_"]');
        if (textHeadingElement) {
          accordionTitle = textHeadingElement.innerText;
          accordionTitleElement.innerText = accordionTitle;
        }
      }

      if (accordionTitle != '') {
        const shopifyAppBlock = appBlockHolder.querySelector('.shopify-app-block');
        const accordionContentElement = appAccordionHolder.querySelector('.accordion__content');
        if (shopifyAppBlock && accordionContentElement) {
          accordionContentElement.appendChild(shopifyAppBlock);
        }

        appAccordionHolder.classList.remove('hidden');
      }
    }
  }
  
  const productInput = shadowRoot.querySelector('#custom\\#product');
  if (productInput) {
    var productTitle = '';
    var productTitleElement = document.querySelector('.product-info__title');
    if (productTitleElement) {
      productTitle = productTitleElement.innerText;
    }
    const productURL = window.location.href;

    productInput.value = productTitle + ': ' + productURL;
    productInput.dispatchEvent(new Event('input', { bubbles: true }));
    productInput.dispatchEvent(new Event('change', { bubbles: true }));

    productInput.parentElement.style.display = 'none';
  }

  const quantityInput = shadowRoot.querySelector('#custom\\#quantity');
  if (quantityInput) {
    var quantity = 1;
    
    const productFormQuantityInput = document.querySelector('.product-info__quantity-selector input[name="quantity"]');
    if (productFormQuantityInput) {
      quantity = productFormQuantityInput.value;

      productFormQuantityInput.addEventListener('input', (event) => {
        const newQuantity = event.target.value;
        quantityInput.value = newQuantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      productFormQuantityInput.addEventListener('change', (event) => {
        const newQuantity = event.target.value;
        quantityInput.value = newQuantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    quantityInput.value = quantity;
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    // quantityInput.parentElement.style.display = 'none';
  }
}

function raqStartObserving() {
  const container = document.querySelector('.shopify-section--main-product');
  if (!container) {
    return;
  }

  const observer = new MutationObserver((mutationsList, observer) => {
    const formEmbedHost = container.querySelector('#app-embed');
    if (formEmbedHost) {
      raqCustomizeFormEmbed(formEmbedHost);
      observer.disconnect();
    }
  });

  const config = { childList: true, subtree: true }; 
  observer.observe(container, config);
}
raqStartObserving();
/* end Draft order */

/* Raptor Recommendations Web Component */
class RaptorRecommendations extends HTMLElement {
  constructor() {
    super();
    this.raptorModule = this.getAttribute('raptor-module');
    this.recommendationsCount = parseInt(this.getAttribute('recommendations-count')) || 6;
    this.apiKey = this.getAttribute('api-key');
    this.customerId = this.getAttribute('customer-id');
    this.apiParams = this.getAttribute('api-params') || '';
    this.productSku = this.getAttribute('product-sku') || '';
    this.debugMode = this.getAttribute('debug-mode') === 'true';
    this.productList = this.querySelector('product-list');
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return '';
  }

  processApiParams() {
    if (!this.apiParams) return '';

    let processed = this.apiParams;

    // Replace [cookie:name] with actual cookie value
    const cookieRegex = /\[cookie:([^\]]+)\]/g;
    processed = processed.replace(cookieRegex, (match, cookieName) => {
      const cookieValue = this.getCookie(cookieName);
      if (this.debugMode) {
        console.log(`Raptor: Cookie ${cookieName} =`, cookieValue);
      }
      return encodeURIComponent(cookieValue);
    });

    // Replace [product.sku] with current variant SKU
    // Note: In Raptor's context, ProductId refers to variant SKU
    processed = processed.replace(/\[product\.sku\]/g, this.productSku);

    if (this.debugMode) {
      console.log('Raptor: Processed params:', processed);
    }
    return processed;
  }

  connectedCallback() {
    // Debug mode: use hardcoded test data
    if (this.debugMode) {
      console.log('Raptor Recommendations: Running in DEBUG mode');
      this.fetchRecommendationsDebug();
      return;
    }

    if (!this.apiKey || !this.customerId || !this.raptorModule) {
      this.showError('Raptor API credentials or module name not configured');
      return;
    }
    this.fetchRecommendations();
  }

  async fetchRecommendationsDebug() {
    try {
      // Test data with SKUs from your test set (matching actual Raptor response format)
      const testData = [
        {"RecommendedId":"AC04147", "Priority":"1"},
        {"RecommendedId":"AC04146", "Priority":"2"},
        {"RecommendedId":"AC04145", "Priority":"3"},
        {"RecommendedId":"AC04181", "Priority":"4"},
        {"RecommendedId":"AC04184", "Priority":"5"},
        {"RecommendedId":"AC04189", "Priority":"6"},
        {"RecommendedId":"AC04185", "Priority":"7"},
        {"RecommendedId":"AC04186", "Priority":"8"},
        {"RecommendedId":"AC04188", "Priority":"9"},
        {"RecommendedId":"AC04187", "Priority":"10"},
        {"RecommendedId":"AC04190", "Priority":"11"}
      ];

      console.log('Debug: Using test SKUs:', testData.map(item => item.RecommendedId));

      const productSkus = this.extractProductSkus(testData);
      
      if (productSkus.length === 0) {
        this.hideSection();
        return;
      }

      await this.fetchAndRenderProducts(productSkus);
    } catch (error) {
      console.error('Raptor recommendations debug error:', error);
      this.showError('Unable to load recommendations (debug mode)');
    }
  }

  async fetchRecommendations() {
    try {
      // Build base URL
      let raptorUrl = `https://api.raptorsmartadvisor.com/v1/${this.customerId}/${this.raptorModule}/${this.recommendationsCount}/${this.apiKey}`;
      
      // Add query parameters if configured
      const params = this.processApiParams();
      if (params) {
        raptorUrl += `?${params}`;
      }
      
      const raptorResponse = await fetch(raptorUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!raptorResponse.ok) {
        throw new Error(`Raptor API error: ${raptorResponse.status}`);
      }

      const raptorData = await raptorResponse.json();
      
      if (!raptorData || raptorData.length === 0) {
        this.hideSection();
        return;
      }

      const productSkus = this.extractProductSkus(raptorData);
      
      if (productSkus.length === 0) {
        this.hideSection();
        return;
      }

      await this.fetchAndRenderProducts(productSkus);
    } catch (error) {
      console.error('Raptor recommendations error:', error);
      this.showError('Unable to load recommendations');
    }
  }

  extractProductSkus(raptorData) {
    const skus = [];
    
    for (const item of raptorData) {
      // Raptor returns RecommendedId which contains the variant SKU
      if (item.RecommendedId) {
        skus.push(item.RecommendedId);
      }
    }
    
    return skus;
  }

  async fetchAndRenderProducts(skus) {
    const searchQuery = skus.map(sku => `variants.sku:${sku}`).join(' OR ');
    const searchUrl = `${window.Shopify.routes.root}search?view=raptor&type=product&q=${encodeURIComponent(searchQuery)}`;

    if (this.debugMode) {
      console.log('Raptor: Searching for', skus.length, 'products');
      console.log('Raptor: Search query:', searchQuery);
      console.log('Raptor: Search URL:', searchUrl);
    }

    const response = await fetch(searchUrl);
    
    if (this.debugMode) {
      console.log('Raptor: Search response status:', response.status);
    }
    
    if (!response.ok) {
      throw new Error(`Search API error: ${response.status}`);
    }

    const html = await response.text();
    
    if (this.debugMode) {
      console.log('Raptor: Received HTML length:', html.length);
      console.log('Raptor: HTML preview:', html.substring(0, 200));
    }
    
    if (html.trim() === '') {
      if (this.debugMode) {
        console.warn('Raptor: Empty search results, hiding section');
      }
      this.hideSection();
      return;
    }

    this.productList.innerHTML = html;
    
    if (this.debugMode) {
      console.log('Raptor: Products rendered successfully');
    }

    // Add click tracking to recommendation links
    this.attachClickTracking();

    const scrollCarousel = this.closest('.floating-controls-container, .scrollable-with-controls')?.querySelector('scroll-carousel');
    if (scrollCarousel) {
      scrollCarousel.classList.add('is-scrollable');
    }
  }

  attachClickTracking() {
    const recommendationItems = this.productList.querySelectorAll('.raptor-recommendation-item');
    
    recommendationItems.forEach((item) => {
      const productSku = item.dataset.productSku;
      const productUrl = item.dataset.productUrl;
      
      // Find all links within this recommendation item
      const links = item.querySelectorAll('a');
      
      links.forEach((link) => {
        link.addEventListener('click', (e) => {
          // Only track if we have the Shopify analytics API and required data
          if (window.Shopify && window.Shopify.analytics && productSku && this.raptorModule) {
            e.preventDefault();
            
            if (this.debugMode) {
              console.log('Raptor: Tracking recommendation click', {
                productId: productSku,
                moduleName: this.raptorModule,
                productUrl: productUrl
              });
            }

            // Publish custom event to Shopify pixel
            window.Shopify.analytics.publish('custom_recommendation_click', {
              productId: productSku,
              moduleName: this.raptorModule,
              productUrl: productUrl
            });

            // Fallback redirect if pixel doesn't handle it
            setTimeout(() => {
              window.location.href = link.href;
            }, 300);
          }
        });
      });
    });
  }

  showError(message) {
    if (this.productList) {
      this.productList.innerHTML = `<div class="error-message"><p>${message}</p></div>`;
    }
  }

  hideSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = 'none';
    }
  }
}

customElements.define('raptor-recommendations', RaptorRecommendations);
/* end Raptor Recommendations */
